import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import axios from 'axios';

const SOURCE_OPTIONS = [
  { value: 'csv_upload', label: 'CSV Upload' },
  { value: 'xlsx_upload', label: 'Excel (.xlsx) Upload' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'crm', label: 'CRM' },
  { value: 'api', label: 'API' },
  { value: 'manual', label: 'Manual Entry' },
];

const SYNC_SOURCES = ['shopify', 'crm', 'api'];
const UPLOAD_SOURCES = ['csv_upload', 'xlsx_upload'];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const CreateAudienceModal = ({ onClose, onSuccess }) => {
  const [audienceName, setAudienceName] = useState('');
  const [source, setSource] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [fileKey, setFileKey] = useState(null);
  const fileInputRef = useRef(null);

  const isAutoSync = SYNC_SOURCES?.includes(source);
  const showUpload = UPLOAD_SOURCES?.includes(source);

  const canCreate =
    audienceName?.trim() !== '' &&
    source !== '' &&
    (!UPLOAD_SOURCES?.includes(source) || fileKey !== null) &&
    !uploading &&
    !creating;

  const uploadToS3 = async (file) => {
    setUploading(true);
    setFileError('');
    try {
      // Step 1: Get presigned URL
      const isXlsx = file.name?.toLowerCase().endsWith('.xlsx');
      const { data: presignData } = await api.post(`/api/v1/audience/presigned-url${isXlsx ? '?fileName=' + encodeURIComponent(file.name) : ''}`);
      const { uploadUrl, fileKey: key } = presignData?.data || {};

      // Step 2: Upload to S3
      await axios.put(uploadUrl, file, {
        headers: { 'Content-Type': isXlsx ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv' },
      });

      setFileKey(key);
      const estimatedRows = Math.floor(file?.size / 80);
      setUploadedFile({ name: file?.name, size: file?.size, rows: estimatedRows });
    } catch (err) {
      console.error('Upload failed:', err);
      setFileError('Failed to upload file. Please try again.');
      setUploadedFile(null);
      setFileKey(null);
    } finally {
      setUploading(false);
    }
  };

  const processFile = (file) => {
    setFileError('');
    const validTypes = [
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const validExts = ['.csv', '.xlsx'];
    const ext = file?.name?.slice(file?.name?.lastIndexOf('.'))?.toLowerCase();
    if (!validTypes?.includes(file?.type) && !validExts?.includes(ext)) {
      setFileError('Only CSV or Excel (.xlsx) files are supported.');
      return;
    }
    if (file?.size > 100 * 1024 * 1024) {
      setFileError('File size must be under 100 MB.');
      return;
    }
    uploadToS3(file);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) processFile(file);
  };

  const handleFileInput = (e) => {
    const file = e?.target?.files?.[0];
    if (file) processFile(file);
    e.target.value = '';
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      await api.post('/api/v1/audience', {
        name: audienceName?.trim(),
        source,
        fileKey: fileKey || null,
      });
      onSuccess?.();
    } catch (err) {
      console.error('Create audience failed:', err);
      setFileError(err?.response?.data?.message || 'Failed to create audience.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create New Audience</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Create a reusable audience list that can be used across multiple campaigns.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Section 1 — Audience Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Audience Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={audienceName}
              onChange={(e) => setAudienceName(e?.target?.value)}
              placeholder="Example: July Cart Abandonment Customers"
              className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors placeholder-gray-400"
            />
            <p className="text-xs text-gray-400 mt-1.5">Give your audience a descriptive name.</p>
          </div>

          {/* Section 2 — Source */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Source <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={source}
                onChange={(e) => { setSource(e?.target?.value); setUploadedFile(null); setFileError(''); setFileKey(null); }}
                className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors appearance-none bg-white text-gray-700"
              >
                <option value="">Select a source</option>
                {SOURCE_OPTIONS?.map((opt) => (
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
              <Icon name="ChevronDown" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            {isAutoSync && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
                <Icon name="RefreshCw" size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  This audience will be synced automatically from the selected source.
                </p>
              </div>
            )}
          </div>

          {/* Section 3 — Upload File (CSV only) */}
          {showUpload && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Upload File <span className="text-red-500">*</span>
              </label>

              {uploading ? (
                <div className="border-2 border-dashed border-blue-300 bg-blue-50 rounded-xl p-8 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                    <p className="text-sm font-medium text-blue-700">Uploading file...</p>
                  </div>
                </div>
              ) : !uploadedFile ? (
                <div
                  onDragOver={(e) => { e?.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef?.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-blue-400 bg-blue-50' :'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Icon name="Upload" size={22} className="text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Drag &amp; Drop your CSV or Excel file here
                      </p>
                      <p className="text-xs text-gray-400 mt-1">or</p>
                    </div>
                    <button
                      type="button"
                      className="px-4 py-2 bg-[#2F55FF] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Browse Files
                    </button>
                    <p className="text-xs text-gray-400">
                      Supported: CSV (.csv), Excel (.xlsx) · Max 100 MB
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx"
                    className="hidden"
                    onChange={handleFileInput}
                  />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Icon name="FileText" size={18} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{uploadedFile?.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        ~{uploadedFile?.rows?.toLocaleString('en-IN')} rows · {formatFileSize(uploadedFile?.size)}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Icon name="CheckCircle" size={13} className="text-green-500" />
                        <span className="text-xs font-medium text-green-600">Uploaded to cloud</span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setUploadedFile(null); setFileError(''); setFileKey(null); }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
                    >
                      Replace File
                    </button>
                  </div>
                </div>
              )}

              {fileError && (
                <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} />
                  {fileError}
                </p>
              )}

              {uploadedFile && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl">
                  <Icon name="Info" size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Contacts will be validated after creation. You'll see real counts once processing completes.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!canCreate}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl transition-colors ${
              canCreate
                ? 'bg-[#2F55FF] text-white hover:bg-blue-700 shadow-sm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {creating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Icon name="Plus" size={15} />
                Create Audience
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAudienceModal;
